import * as React from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { Box, Button, Text, Touchable, useToggle } from '@factorialco/deprecated-design-system'

import FormRow from 'modules/core/components/FormRow'
import RadioInput from 'modules/core/components/Inputs/FilterSelect/SelectOption/RadioInput'
import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'

import { usePerformanceModuleContext } from 'modules/performance/hooks/usePerfomanceModuleContext'
import { canCreateReviews } from 'modules/reviews/lib/canCreateReviews'
import { templateRevieweeOptions } from 'modules/reviews/lib/templateRevieweeOptions'

export const NewPerformanceReviewFromTemplate: React.FC<{
  onClose: () => void
}> = ({ onClose: onModalClose }) => {
  const { navigation } = useNavigation()

  const [reviewSelection, toggleReviewSelection] = useToggle(true)
  const { hasFullVisibility } = usePerformanceModuleContext()
  const navigate = useNavigate()
  const [loading, setLoading] = React.useState(false)
  const [participants, setParticipants] = React.useState<string>(
    templateRevieweeOptions(hasFullVisibility)[0]?.value
  )

  const onClose = () => {
    track(trackingEvents.reviews.startReviewCancel)
    onModalClose()
  }

  const submit = async () => {
    setLoading(true)
    const { success, value } = await asyncAction(
      () =>
        ApiClient().post('/performance_reviews/reviews/create_from_template', {
          target: participants,
          respondents: {
            self: reviewSelection,
            manager: true,
          },
        }),
      {
        successMessage: i18n.t('common.saved_successfully'),
        errorFlash: true,
      }
    )
    setLoading(false)
    track(trackingEvents.reviews.useReviewTemplates, {
      target: participants,
      self: reviewSelection,
      manager: true,
      is_successful: success,
      error_code: !success && JSON.stringify(value),
    })
    if (success) {
      navigate(navigation.performance.reviews.id.configuration.fullPath({ id: value.id }))
    }
  }

  if (!canCreateReviews()) {
    return <Navigate to={navigation.performance.reviews.fullPath()}></Navigate>
  }

  const onChangeParticipants = (value: string) => {
    setParticipants(value)
  }

  return (
    <Modal
      title={i18n.t('surveys.performance.participants.title')}
      description={i18n.t('surveys.performance.participants.description')}
      illustration="surveys"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <Button disabled={loading} submit>
          {i18n.t('surveys.performance.participants.review_create_action')}
        </Button>
      }
      progress={{ amount: 2, total: 2 }}
    >
      <Box paddingBottom="s6">
        <Text.Normal weight="semibold">
          {i18n.t('surveys.performance.participants.reviewees')}
        </Text.Normal>
      </Box>
      <FormRow>
        <Select
          options={templateRevieweeOptions(hasFullVisibility).map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          onChange={(value) => onChangeParticipants(value)}
          value={participants}
          readonly={templateRevieweeOptions(hasFullVisibility).length < 2}
        />
      </FormRow>
      <Box paddingBottom="s6">
        <Text.Normal weight="semibold">
          {i18n.t('surveys.performance.participants.reviewers')}
        </Text.Normal>
      </Box>
      <FormRow>
        <Box
          gap="s16"
          border={{
            all: {
              width: 's2',
              style: 'solid',
              color: reviewSelection ? 'viridian1000' : 'grey400',
            },
          }}
          borderRadius={{
            all: 'abs008',
          }}
          padding="s16"
        >
          <Box flexDirection="row" justifyContent="spaceBetween">
            <Text.Normal weight="semibold">
              {i18n.t('surveys.performance.participants.manager_self_review_title')}
            </Text.Normal>
            <Touchable onClick={toggleReviewSelection(true)}>
              <RadioInput selected={reviewSelection} />
            </Touchable>
          </Box>
          <Text.Normal>
            {i18n.t('surveys.performance.participants.manager_self_review_description')}
          </Text.Normal>
        </Box>
      </FormRow>
      <FormRow>
        <Box
          gap="s16"
          border={{
            all: {
              width: 's2',
              style: 'solid',
              color: !reviewSelection ? 'viridian1000' : 'grey400',
            },
          }}
          borderRadius={{
            all: 'abs008',
          }}
          padding="s16"
        >
          <Box flexDirection="row" justifyContent="spaceBetween">
            <Text.Normal weight="semibold">
              {i18n.t('surveys.performance.participants.manager_review_title')}
            </Text.Normal>
            <Touchable onClick={toggleReviewSelection(false)}>
              <RadioInput selected={!reviewSelection} />
            </Touchable>
          </Box>
          <Text.Normal>
            {i18n.t('surveys.performance.participants.manager_review_description')}
          </Text.Normal>
        </Box>
      </FormRow>
      <FormRow>
        <Box
          gap="s16"
          background="grey300"
          padding="s16"
          borderRadius={{
            all: 'abs008',
          }}
        >
          <Text.Normal>{i18n.t('surveys.performance.participants.review_disclaimer')}</Text.Normal>
        </Box>
      </FormRow>
    </Modal>
  )
}
